/* Phase 6 · Maintenance — Export snapshot, CSV shortcuts, nuclear reset */
import React, { useState } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { AlertTriangle, Trash2, Upload, Download, ClipboardList } from "lucide-react";

export default function Maintenance() {
  const [confirm, setConfirm] = useState("");
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const [snapping, setSnapping] = useState(false);

  const doSnapshot = async () => {
    setSnapping(true);
    try {
      const r = await fetch(`${API}/admin/export-snapshot`, { credentials: "include" });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const disp = r.headers.get("Content-Disposition") || "";
      const m = disp.match(/filename="([^"]+)"/);
      const filename = m ? m[1] : `hag_snapshot_${Date.now()}.zip`;
      const blob = await r.blob();
      const u = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = u; a.download = filename; a.click();
      URL.revokeObjectURL(u);
      toast.success("Snapshot exported");
    } catch (e) {
      toast.error(e.message || "Snapshot failed");
    }
    setSnapping(false);
  };

  const doReset = async () => {
    if (confirm !== "RESET") return toast.error("Type RESET to confirm");
    if (!window.confirm("BREAK-GLASS ONLY. This wipes catalogue/operational data. Continue?")) return;
    setRunning(true);
    try {
      const r = await api.post("/admin/nuclear-reset", { confirm });
      setResult(r.data);
      toast.success("Reset complete");
      setConfirm("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
    setRunning(false);
  };

  return (
    <div className="p-4 md:p-6 max-w-3xl" data-testid="maintenance-page">
      <div className="mb-4">
        <div className="text-[11px] uppercase tracking-widest text-[#7C8E9E]">ADMIN · MAINTENANCE</div>
        <h1 className="text-2xl md:text-3xl font-bold text-[#0B2340]">Maintenance</h1>
        <p className="text-xs text-[#4A627A] mt-1">
          Always export a snapshot before any replace-mode import. Follow the{" "}
          <Link to="/admin/refresh" className="underline font-semibold text-[#B08B49]" data-testid="mnt-refresh-link">
            refresh checklist
          </Link>{" "}
          for a scoped, reversible catalogue refresh.
        </p>
      </div>

      {/* 1. Snapshot */}
      <div className="bg-white p-5 rounded-lg border border-slate-100 mb-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-700 flex-shrink-0">
            <Download size={18} />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-[#0B2340]">Export snapshot</h3>
            <p className="text-xs text-[#4A627A] mt-1">
              Downloads a ZIP with CSVs for <code>pos, carpet_lines, client_orders, weavers, looms, vendors, mg_pieces</code>.
              Always export before any replace-mode import.
            </p>
            <button onClick={doSnapshot} disabled={snapping} data-testid="mnt-snapshot-btn"
              className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-white text-sm font-semibold disabled:opacity-40"
              style={{ background: "var(--brand-navy)" }}>
              <Download size={13} /> {snapping ? "Exporting…" : "Export snapshot ZIP"}
            </button>
          </div>
        </div>
      </div>

      {/* 2. Refresh checklist quick link */}
      <div className="bg-white p-5 rounded-lg border border-slate-100 mb-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center text-amber-700 flex-shrink-0">
            <ClipboardList size={18} />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-[#0B2340]">Refresh checklist</h3>
            <p className="text-xs text-[#4A627A] mt-1">
              Ordered runbook for the scoped refresh — export → import vendors → weavers → looms → PO replace → verify.
              Live status against your DB.
            </p>
            <Link to="/admin/refresh" data-testid="mnt-checklist-link"
              className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold text-white"
              style={{ background: "#B08B49" }}>
              Open checklist →
            </Link>
          </div>
        </div>
      </div>

      {/* 3. CSV upload shortcut */}
      <div className="bg-white p-5 rounded-lg border border-slate-100 mb-4">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center text-blue-700 flex-shrink-0">
            <Upload size={18} />
          </div>
          <div>
            <h3 className="font-semibold text-[#0B2340]">Bulk import via CSV</h3>
            <p className="text-xs text-[#7C8E9E]">
              Upload staff, weavers, looms, vendors, job workers, client orders, MG pieces, RIC yarn. Download a sample per kind.
              (Purchase Orders use the dedicated Data Import screen.)
            </p>
            <Link to="/admin/csv-upload" data-testid="mnt-csv-link"
              className="mt-2 inline-block text-xs text-[#B08B49] font-semibold underline">
              Go to CSV upload →
            </Link>
          </div>
        </div>
      </div>

      {/* 4. Nuclear reset — break glass */}
      <div className="bg-white p-5 rounded-lg border border-red-200">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center text-red-700 flex-shrink-0">
            <AlertTriangle size={18} />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-red-800">Nuclear reset · break-glass only</h3>
            <p className="text-xs text-[#4A627A] mt-1">
              Wipes catalogue + operational data: POs, carpet lines, client orders, weavers, looms, vendors, staff, MG pieces, RIC yarn ledger, etc.
              <br />
              <b>Preserved</b>: logins, audit trail, notifications, vendor magic-link tokens, daura inspections,
              escalations, statutory job-work records and gate passes, visitor logs, weaver aliases.
            </p>
            <div className="mt-3 flex gap-2 items-center flex-wrap">
              <input placeholder="Type RESET to confirm"
                className="border rounded px-2 py-1.5 text-sm font-mono"
                data-testid="mnt-confirm"
                value={confirm} onChange={e => setConfirm(e.target.value.toUpperCase())} />
              <button onClick={doReset} disabled={running || confirm !== "RESET"}
                data-testid="mnt-reset-btn"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-red-600 text-white text-sm font-semibold disabled:opacity-40">
                <Trash2 size={14} /> {running ? "Wiping…" : "Nuclear reset (break-glass only)"}
              </button>
            </div>
          </div>
        </div>

        {result && (
          <div className="mt-4 p-3 rounded bg-slate-50 border border-slate-200 text-xs" data-testid="mnt-result">
            <div className="font-semibold mb-1">Wiped at {result.at?.slice(11, 19)}</div>
            <div className="max-h-40 overflow-y-auto font-mono text-[11px] text-[#4A627A]">
              {Object.entries(result.wiped_counts).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
                <div key={k}>{k}: {v}</div>
              ))}
            </div>
            <div className="mt-1 text-[10px] text-[#7C8E9E]">
              Preserved: {result.kept.join(", ")}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
